import { runInAction } from 'mobx';
import Api from '../../../services/Api';
import SiteMapModel from '../../Sitemap/models/SiteMapModel';
import TreeLinkedList from '../../Sitemap/models/TreeLinkedList';
import WebsitePreviewCreator from '../services/WebsitePreviewCreator';
import Observable from '../../../decorators/Observable';
import Action from '../../../decorators/Action';
import AbstractSessionModel from '../../../models/AbstractSessionModel';
import { ColorScheme } from '../../Design/view/files/ImageWebsiteLogo';
import Computed from '../../../decorators/Computed';
import StoreFieldInSession from '../../../decorators/StoreFieldInSession';
import BusinessModel from '../../FindYourBusiness/models/BusinessModel';
import SiteBuilderWizardData from './SiteBuilderWizardData';
import WizardSessionData from '../../../services/WizardSessionData';
import { IWizardDataModel } from '../../../models/IWizardDataModel';
import PageGeneratedData from './generated-data/PageGeneratedData';
import TemplateGeneratedData from './generated-data/TemplateGeneratedData';
import BusinessWizardData from '../../FindYourBusiness/models/BusinessWizardData';

class SiteBuilderModel
  extends AbstractSessionModel
  implements IWizardDataModel
{
  @StoreFieldInSession({
    serialize: (value: any) => {
      return JSON.stringify(value.map((item) => item.serialize()));
    },
    deserialize(value: any) {
      let object = JSON.parse(value);
      return object.map((item) => {
        let page = new PageGeneratedData();
        page.deserialize(item);
        return page;
      });
    }
  })
  @Observable()
  generatedPages: PageGeneratedData[] = [];

  @StoreFieldInSession({
    serialize: (item: any) => {
      return item?.serialize() || null;
    },
    deserialize(value: any) {
      if (value === null) {
        return null;
      }

      let page = new PageGeneratedData();
      page.deserialize(value);
      return page;
    }
  })
  @Observable()
  currentShownInPreviewGeneratedPage: PageGeneratedData = null;

  @StoreFieldInSession({
    serialize: (item: any) => {
      return item?.serialize() || null;
    },
    deserialize(value: any) {
      if (value === null) {
        return null;
      }

      let page = new TemplateGeneratedData();
      page.deserialize(value);
      return page;
    }
  })
  @Observable()
  templateGeneratedData: TemplateGeneratedData = null;

  /**
   * Current selected device for preview
   */
  @StoreFieldInSession()
  @Observable()
  currentSelectedDevice: string = 'desktop';

  @StoreFieldInSession({
    reactOn: (value) => {
      // basically, we react only on changes of the treeLinkList.head value.
      // when the user change the structure of the tree, we call updateSitemap function
      // and the treeLinkList.head value is changed,
      // which triggers the re-render of the tree component
      // and fire reaction in AbstractSessionModel
      return value?.toJSON() || null;
    },
    serialize: (value: any) => {
      const list = value?.serialize() || JSON.stringify([]);

      return JSON.stringify({
        list,
        head: value?.getCurrentPointer()?.getId()
      });
    },
    deserialize(value: any) {
      let object = JSON.parse(value);
      const { list, head } = object;

      let treeLinkList = new TreeLinkedList();

      if (list) {
        const parsed = JSON.parse(list);
        if (Array.isArray(parsed) && parsed.length > 0) {
          treeLinkList.deserialize(parsed);
        }
      }

      if (head) {
        treeLinkList.setCurrentPointer(head);
      }

      return treeLinkList;
    }
  })
  @Observable()
  pageTreeLinkList: TreeLinkedList = null;

  @StoreFieldInSession()
  @Observable()
  processedPages: any = [];

  @StoreFieldInSession()
  @Observable()
  iframeVisibility = true;

  /**
   * This is the generated theme config that will be used to
   * generate the preview website
   */
  @StoreFieldInSession()
  @Observable()
  generatedThemeConfig: Record<string, any> = null;

  /**
   * We are stroing the user preview data uuid here, then
   * this uuid will be used to generate the preview website url
   */
  @StoreFieldInSession()
  @Observable()
  userPreviewDataUuid: string = null;

  @StoreFieldInSession()
  @Observable()
  finalColorScheme: ColorScheme = null;

  @StoreFieldInSession()
  @Observable()
  isPreviewTourOpen = true;

  @Observable()
  siteBuilderWizardData = new SiteBuilderWizardData();

  @Observable()
  pictureModalOpenForPictureId: number = null;

  @Observable()
  templateDataLoading = false;

  @Observable()
  iframeLoaded = false;

    // Font selection for theme (main/display)
    @StoreFieldInSession()
    @Observable()
    fontMap: {
      main: { name: string };
      display: { name: string };
    } = null;

  constructor() {
    super();
    super.initFields();
  }

  ////////////////////////////////////////////
  // Setters
  ////////////////////////////////////////////

  @Action()
  public setPreviewTourOpen(open: boolean) {
    this.isPreviewTourOpen = open;
  }

    @Action()
    public setFontMap(fontMap: { main: { name: string }; display: { name: string } }) {
      this.fontMap = fontMap;
    }

  @Action()
  public setPictureUploadModalOpen(pictureId: number) {
    this.pictureModalOpenForPictureId = pictureId;
  }

  @Action()
  public setIframeLoaded(loaded: boolean) {
    this.iframeLoaded = loaded;
  }

  @Action()
  public setIframeVisibility(visible: boolean) {
    this.iframeVisibility = visible;
  }

  @Action()
  public setCurrentSelectedDevice(device: string) {
    this.currentSelectedDevice = device;
  }

  @Action()
  public setFinalColorScheme(colorScheme: ColorScheme) {
    this.finalColorScheme = colorScheme;
  }

  @Action()
  public setPageTreeLinkList(treeLinkList: TreeLinkedList) {
    this.pageTreeLinkList = treeLinkList;
  }

  @Action()
  public setGeneratedPages(pages: PageGeneratedData[]) {
    this.generatedPages = pages;
  }

  @Action()
  public setCurrentShownInPreviewGeneratedPage(page: PageGeneratedData) {
    this.currentShownInPreviewGeneratedPage = page;
  }

  @Action()
  public setTemplateGeneratedData(template: TemplateGeneratedData) {
    this.templateGeneratedData = template;
  }

  ////////////////////////////////////////////
  // Loaders
  ////////////////////////////////////////////

  @Action()
  public async loadCssThemeConfig(
    theme: ColorScheme,
    fontMap: {
      main: { name: string };
      display: { name: string };
    }
  ) {
    let themeVariables = await Api.getThemeVariables(
      BusinessModel.businessWizardData.category,
      theme,
      fontMap
    );

    if (themeVariables.data?.theme === void 0) {
      throw new Error('Theme not found');
    }

    const previewData = await Api.saveUserPreviewData({
      navigationData: SiteMapModel.getSiteMapFinalTree({
        excludeDefaultPages: true
      }),
      themeConfigData: themeVariables.data?.theme,
      openingHours: BusinessModel.getWizardData().opening_hours,
      openingHoursFormatted:
        BusinessModel.getWizardData().opening_hours_formatted
    });

    runInAction(() => {
      this.generatedThemeConfig = themeVariables.data?.theme;
      this.userPreviewDataUuid = previewData.data.uuid;
    });
  }

  @Action()
  public async loadTemplateData(): Promise<void> {
    runInAction(() => {
      this.templateDataLoading = true;
    });

    let data = await Api.getTemplateData(
      BusinessModel.businessWizardData.category,
      SiteMapModel.getSiteMapFinalTree(),
      WebsitePreviewCreator.collectBusinessData()
    );

    runInAction(() => {
      this.templateDataLoading = false;
    });

    return data.data;
  }

  ////////////////////////////////////////////
  // Actions
  ////////////////////////////////////////////

  private findGeneratedPageById(pageId: number | string) {
    return this.generatedPages.find((page) => page.getId() === pageId);
  }

  @Action()
  public moveToCustomPage(pageId: any) {
    const generatedPage = this.findGeneratedPageById(pageId);
    this.currentShownInPreviewGeneratedPage = generatedPage;
  }

  @Action()
  public async updatePreviewData() {
    const previewData = await Api.saveUserPreviewData({
      navigationData: SiteMapModel.getSiteMapFinalTree({
        excludeDefaultPages: true
      }),
      themeConfigData: this.generatedThemeConfig,
      openingHours: BusinessModel.getWizardData().opening_hours,
      openingHoursFormatted:
        BusinessModel.getWizardData().opening_hours_formatted
    });

    runInAction(() => {
      this.userPreviewDataUuid = previewData.data.uuid;
    });
  }

  ////////////////////////////////////////////
  // Getters
  ////////////////////////////////////////////

  @Computed()
  get iframeUrl() {
    let currentSelectedPagePath =
      this.currentShownInPreviewGeneratedPage.getPagePath() || '';

    if (currentSelectedPagePath === '/') {
      currentSelectedPagePath = '';
    }

    let templatUrl;
    if (process.env.APPLICATION_ENV === 'development') {
      templatUrl = process.env.TEMPLATE_SITE;
    } else {
      templatUrl = this.templateGeneratedData.getWebsiteTemplate();
    }

    return `${templatUrl}${currentSelectedPagePath}?wizard_theme_preview_session=${encodeURIComponent(
      this.userPreviewDataUuid
    )}&timestamp=${new Date().getTime()}`;
  }

  getWizardData() {
    return this.siteBuilderWizardData.getWizardData();
  }

  getSessionKey(): string {
    return 'SiteBuilderModel';
  }
}

const model = new SiteBuilderModel();
WizardSessionData.addModel(model);
export default model;
